import {ChangeDetectorRef, Component} from '@angular/core';
import {SearchForm} from '../../../sdk/component/search/searchForm';
import {Router} from '@angular/router';
import {SearchService} from '../../../sdk/component/search/search.service';
import {ROUTES} from '../../../app.routes';
import {
    Account,
    AccountPage,
    AccountsSortField,
    BillingType,
    BusinessECommerceStore,
    EcommerceDashboardPeriod,
    EcommercePaymentEvent,
    EcommercePaymentState,
    FileTransfer,
    WebAccountService,
    WebBusinessService,
    WebEcommerceDashboardService
} from 'web-angular-client';
import moment from 'moment/moment';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {AlertService} from '../../../sdk/service/alert.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {finalize} from 'rxjs';
import {saveAs} from 'file-saver';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {MenuItem} from 'primeng/api';
import {
    browserTimezone,
    EcommerceAccountSearchService
} from '../../../sdk/component/search/ecommerce-account-search.service';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {downloadInvoice} from '../../../sdk/util/invoice-download.helper';
import {RefundDialogService} from '../../../sdk/service/refund-dialog.service';
import {isRefundEligible} from '../../../sdk/util/refund-eligibility';
import {sortPaymentEventsNewestFirst} from '../../../sdk/util/ecommerce/payment-events';
import {ECOMMERCE_TRANSACTIONS_PERIOD_OPTIONS} from '../../ecommerce/ecommerce-period-options';
import {TableSortChange} from '../../../sdk/component/table/sortable-table-base';
import {SearchFilterBase} from '../../../sdk/component/search/search-filter-base';

@Component({
    selector: 'app-ecommerce-payment-dashboard',
    templateUrl: './ecommerce-payment-dashboard.component.html',
    styleUrls: ['./ecommerce-payment-dashboard.component.scss'],
    standalone: false
})
export class EcommercePaymentDashboardComponent extends SearchForm
{
    expandedPaymentEventAccountIds = new Set<number>();
    paymentStateByAccountId = new Map<number, EcommercePaymentState>();
    paymentEventsLoadingAccountIds = new Set<number>();
    paymentEventsErrorAccountIds = new Set<number>();
    ecommerceStoreOptions: DropdownItem[] = [];
    storesById = new Map<number, BusinessECommerceStore>();
    readonly periodOptions: DropdownItem[] = ECOMMERCE_TRANSACTIONS_PERIOD_OPTIONS;
    selectedPeriod: EcommerceDashboardPeriod = EcommerceDashboardPeriod.LAST_30_D;
    csvDownloading = false;

    constructor(
        private router: Router,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private webAccountService: WebAccountService,
        private webEcommerceDashboardService: WebEcommerceDashboardService,
        private ecommerceAccountSearchService: EcommerceAccountSearchService,
        protected searchService: SearchService,
        private ngxLoader: NgxUiLoaderService,
        private alertService: AlertService,
        private cdr: ChangeDetectorRef,
        private webBusinessService: WebBusinessService,
        private refundDialogService: RefundDialogService)
    {
        super(authenticationService, ecommerceAccountSearchService, searchService, environmentService);
    }

    get contents(): Account[]
    {
        const ecommercePage = this.results as AccountPage;
        return ecommercePage?.content ?? [];
    }

    override ngOnInit(): void
    {
        super.ngOnInit();
        this.loadEcommerceStores();
        this.ecommerceAccountSearchService.currentPeriod = null;
    }

    /**
     * The search service is root-scoped — leaving {@code currentPeriod} set would carry this page's period
     * filter into any later consumer that reuses the same service instance. Clear it on teardown so each page
     * starts from a clean slate.
     */
    override ngOnDestroy(): void
    {
        this.ecommerceAccountSearchService.currentPeriod = null;
        super.ngOnDestroy();
    }

    onPeriodChange(period: string): void
    {
        if (!period)
        {
            return;
        }
        this.selectedPeriod = period as EcommerceDashboardPeriod;
    }

    downloadCsv(): void
    {
        if (this.csvDownloading)
        {
            return;
        }
        this.csvDownloading = true;
        this.webEcommerceDashboardService.exportEcommerceDashboardTransactions(
            this.selectedPeriod,
            undefined,
            undefined,
            browserTimezone()
        ).subscribe({
            next: (file: FileTransfer) =>
            {
                this.csvDownloading = false;
                if (!file?.fileContent)
                {
                    this.alertService.info('No transactions available for the selected period.');
                    return;
                }
                try
                {
                    const binary = atob(file.fileContent);
                    const bytes = Uint8Array.from(binary, c => c.codePointAt(0));
                    const blob = new Blob([bytes], {type: 'text/csv;charset=utf-8'});
                    saveAs(blob, `${file.fileName}.${file.fileExtension}`);
                }
                catch
                {
                    this.alertService.error('Failed to export transactions, please contact support for assistance.');
                }
            },
            error: () =>
            {
                this.csvDownloading = false;
                this.alertService.error('Failed to export transactions, please contact support for assistance.');
            }
        });
    }

    onSortChange(event: TableSortChange, searchComponent: SearchFilterBase): void
    {
        this.searchService.field = event.field;
        this.searchService.direction = event.order;
        searchComponent.refresh();
    }

    readonly buildMenuItemsForAccount = (account: Account): MenuItem[] =>
    {
        const items: MenuItem[] = [
            {
                label: 'View',
                icon: 'fa fa-eye fa-lg',
                command: () => this.view(account)
            },
            {
                label: 'Invoice',
                icon: 'fa fa-download fa-lg',
                command: () => this.download(account),
                disabled: !this.isInvoiceBillingType(account?.billingType)
            }
        ];
        if (isRefundEligible(account))
        {
            items.push({
                label: 'Refund',
                icon: 'fa fa-undo fa-lg',
                command: () => this.refundDialogService.open(account)
            });
        }
        return items;
    };

    readonly isAccountExpanded = (account: Account): boolean =>
        !!account?.id && this.expandedPaymentEventAccountIds.has(account.id);

    onRowClick(account: Account): void
    {
        if (account?.id != null)
        {
            this.togglePaymentEvents(account.id);
        }
    }

    create()
    {
        // not used but required to be implemented
    }

    view(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.router.navigate([ROUTES.SECURE.ACCOUNT.VIEW.pathSansParameters, account.id]).then();
    }

    getDefaultSortField(): string
    {
        return AccountsSortField.ID;
    }

    download(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        downloadInvoice(
            account.id,
            {
                invoiceLoader: id => this.webAccountService.getInvoiceByAccountId(id),
                onStart: () => this.ngxLoader.start(),
                onComplete: () => this.ngxLoader.stop(),
                onError: message => this.alertService.error(message)
            });
    }

    /**
     * @return <i>true</i> if {@link BillingType} is INVOICE or null.
     * Invoice is the default type, so a <i>null</i> value always defaults to invoice.
     */
    isInvoiceBillingType(billingType: BillingType | null | undefined): boolean
    {
        return billingType == null || billingType === BillingType.INVOICE;
    }

    getPaymentState(account: Account): EcommercePaymentState | undefined
    {
        return this.paymentStateByAccountId.get(account?.id);
    }

    getPaymentEvents(account: Account): EcommercePaymentEvent[]
    {
        return sortPaymentEventsNewestFirst(this.getPaymentState(account)?.events);
    }

    togglePaymentEvents(accountId: number)
    {
        if (this.expandedPaymentEventAccountIds.has(accountId))
        {
            this.expandedPaymentEventAccountIds.delete(accountId);
        }
        else
        {
            this.expandedPaymentEventAccountIds.add(accountId);
            this.loadPaymentEvents(accountId);
        }
    }

    isPaymentEventsLoading(accountId: number): boolean
    {
        return this.paymentEventsLoadingAccountIds.has(accountId);
    }

    hasPaymentEventsError(accountId: number): boolean
    {
        return this.paymentEventsErrorAccountIds.has(accountId);
    }

    private loadEcommerceStores()
    {
        this.webBusinessService.getECommerceStoresForBusiness().subscribe({
            next: (stores: BusinessECommerceStore[]) =>
            {
                const list = stores ?? [];
                this.storesById.clear();
                list.forEach((store) =>
                {
                    if (store.id != null)
                    {
                        this.storesById.set(store.id, store);
                    }
                });
                this.ecommerceStoreOptions = list
                    .map((store) =>
                    {
                        const name = store.eCommerceStoreName ?? `${store.id}`;
                        const label = store.domain ? `${name} – ${store.domain}` : name;
                        return {label, value: store.id};
                    })
                    .filter((store) => store.value != null);
            },
            error: () =>
            {
                this.alertService.error('Failed to load e-commerce stores for the filter.');
            }
        });
    }

    private isFinalizedPayment(paymentState: EcommercePaymentState): boolean
    {
        const status = paymentState?.status;
        return status === EcommercePaymentState.StatusEnum.SUCCESS
            || status === EcommercePaymentState.StatusEnum.EXPIRED
            || status === EcommercePaymentState.StatusEnum.CANCELLED_BY_MERCHANT
            || status === EcommercePaymentState.StatusEnum.CANCELLED_BY_CUSTOMER;
    }

    private hasCachedFinalizedPayment(accountId: number): boolean
    {
        const cached = this.paymentStateByAccountId.get(accountId);
        return cached != null && this.isFinalizedPayment(cached);
    }

    private loadPaymentEvents(accountId: number)
    {
        if (!accountId || this.hasCachedFinalizedPayment(accountId) || this.paymentEventsLoadingAccountIds.has(accountId))
        {
            return;
        }

        this.paymentEventsLoadingAccountIds.add(accountId);
        this.paymentEventsErrorAccountIds.delete(accountId);

        this.webAccountService.getEcommercePaymentState(accountId).pipe(
            finalize(() =>
            {
                this.paymentEventsLoadingAccountIds.delete(accountId);
                this.cdr.markForCheck();
            })
        ).subscribe({
            next: paymentState =>
            {
                this.paymentStateByAccountId.set(accountId, paymentState);
            },
            error: () =>
            {
                this.paymentEventsErrorAccountIds.add(accountId);
                this.alertService.error('Failed to load payment events.');
            }
        });
    }

    private displayGuestPrivilegeError(): void
    {
        this.alertService.error('Guest accounts cannot perform this action.', false);
    }
}
